import { HashRouter, Routes, Route, Navigate, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useCurrentUser } from './hooks/useCurrentUser';
import { useManagerApprovals } from './hooks/useManagerApprovals';
import { useSuperApprovals } from './hooks/useSuperApprovals';
import { useFYIOrders, usePendingApprovals } from './hooks/useOrders';
import { useNotifications } from './hooks/useNotifications';
import { MyTimesheet } from './components/MyTimesheet';
import { ApprovalDashboard } from './components/ApprovalDashboard';
import { SuperApproverDashboard } from './components/SuperApproverDashboard';
import { ProjectsAdmin } from './components/ProjectsAdmin';
import { PeopleAdmin } from './components/PeopleAdmin';
import { RatesAdmin } from './components/RatesAdmin';
import { AssetTimesheets } from './components/AssetTimesheets';
import { Reports } from './components/Reports';
import { Ordering } from './components/ordering/Ordering';
import { UnlockGroupDashboard } from './components/UnlockGroupDashboard';
import { NotificationsPage, NotificationToastContainer } from './components/NotificationsPage';
import { SOFT_LAUNCH } from './utils/constants';

// ─── Loading / Error States ───────────────────────────────────────────────────

function LoadingScreen() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: 12,
        color: 'var(--text-muted)',
      }}
    >
      <div className="spinner" aria-label="Loading" />
      <span style={{ fontSize: 13 }}>Loading Evove Timesheets…</span>
    </div>
  );
}

function AccessDeniedScreen({ error }: { error: Error }) {
  const isNotInPeopleList = error.message.includes('not found in PEOPLE list');
  const isSharePointAccessDenied = error.message.includes('403');

  const icon = isNotInPeopleList ? (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="28" cy="28" r="28" fill="rgba(37,99,235,0.08)" />
      <circle cx="28" cy="22" r="8" stroke="#2563eb" strokeWidth="2.2" fill="none" />
      <path d="M14 44c0-7.732 6.268-14 14-14s14 6.268 14 14" stroke="#2563eb" strokeWidth="2.2" strokeLinecap="round" fill="none" />
      <circle cx="40" cy="38" r="9" fill="#fff" />
      <circle cx="40" cy="38" r="8.5" stroke="#e2e6ed" />
      <path d="M37 38h6M40 35v6" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ) : (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="28" cy="28" r="28" fill="rgba(37,99,235,0.08)" />
      <rect x="19" y="26" width="18" height="14" rx="3" stroke="#2563eb" strokeWidth="2.2" fill="none" />
      <path d="M22 26v-4a6 6 0 0 1 12 0v4" stroke="#2563eb" strokeWidth="2.2" strokeLinecap="round" fill="none" />
      <circle cx="28" cy="33" r="2" fill="#2563eb" />
    </svg>
  );

  const title = isNotInPeopleList
    ? "You're not on the access list"
    : isSharePointAccessDenied
    ? "SharePoint access required"
    : "Unable to load your profile";

  const description = isNotInPeopleList
    ? "Your account hasn't been added to the Evove Timesheets people list yet. Please contact your administrator to get access."
    : isSharePointAccessDenied
    ? "Your account doesn't have permission to access the SharePoint site used by Evove Timesheets. Please contact your administrator."
    : "We couldn't load your profile. This may be a temporary issue — try again or contact your administrator if the problem persists.";

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'var(--bg, #f7f8fa)',
        padding: '24px',
      }}
    >
      <div
        style={{
          background: 'var(--surface, #fff)',
          border: '1px solid var(--border, #e2e6ed)',
          borderRadius: '16px',
          padding: '48px 40px',
          maxWidth: '440px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
        }}
      >
        <div style={{ marginBottom: '24px' }}>{icon}</div>

        <h2
          style={{
            margin: '0 0 12px',
            fontSize: '20px',
            fontWeight: 700,
            color: 'var(--text, #111827)',
            letterSpacing: '-0.01em',
          }}
        >
          {title}
        </h2>

        <p
          style={{
            margin: '0 0 32px',
            fontSize: '14px',
            color: 'var(--text-muted, #6b7280)',
            lineHeight: '1.6',
          }}
        >
          {description}
        </p>

        <div
          style={{
            background: 'rgba(37,99,235,0.06)',
            border: '1px solid rgba(37,99,235,0.15)',
            borderRadius: '10px',
            padding: '16px',
            marginBottom: '28px',
            textAlign: 'left',
          }}
        >
          <p style={{ margin: '0 0 6px', fontSize: '12px', fontWeight: 600, color: 'var(--accent, #2563eb)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            What to do next
          </p>
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: 'var(--text, #111827)', lineHeight: '1.7' }}>
            <li>Contact your system administrator</li>
            <li>Ask them to add you to the {isNotInPeopleList ? 'People list' : 'SharePoint site'}</li>
            <li>Then return here and click <strong>Retry</strong></li>
          </ul>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <button className="btn btn-primary btn-sm" onClick={() => window.location.reload()}>
            Retry
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => {
              sessionStorage.clear();
              window.location.reload();
            }}
          >
            Sign out &amp; retry
          </button>
        </div>

        <p style={{ margin: '20px 0 0', fontSize: '11px', color: 'var(--text-dim, #9ca3af)' }}>
          Evove Timesheets
        </p>
      </div>
    </div>
  );
}

function ErrorScreen({ error }: { error: Error }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div className="err-banner" style={{ maxWidth: 480 }}>
        <strong>Could not load your profile.</strong>&nbsp;{error.message}
      </div>
      <button className="btn btn-ghost btn-sm" onClick={() => window.location.reload()}>
        Retry
      </button>
    </div>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

function Sidebar({
  canApprove,
  isSuperApprover,
  isUnlockGroup,
  isAdmin,
  canViewAssetTimesheets,
  canViewBillOutRates,
  userName,
  approvalCount,
  superApprovalCount,
  fyiCount,
  pendingOrderCount,
  notificationCount,
  isSoftLaunchApprover,
  softLaunchApprovalCount,
}: {
  canApprove: boolean;
  isSuperApprover: boolean;
  isUnlockGroup: boolean;
  isAdmin: boolean;
  canViewAssetTimesheets: boolean;
  canViewBillOutRates: boolean;
  userName: string;
  approvalCount: number;
  superApprovalCount: number;
  fyiCount: number;
  pendingOrderCount: number;
  notificationCount: number;
  isSoftLaunchApprover: boolean;
  softLaunchApprovalCount: number;
}) {
  const [isOpen, setIsOpen] = useState(() => {
    const stored = localStorage.getItem('navOpen');
    return stored === null ? true : stored === 'true';
  });
  const toggleNav = () => setIsOpen(o => {
    const next = !o;
    localStorage.setItem('navOpen', String(next));
    return next;
  });

  return (
    <nav className={isOpen ? 'rail open' : 'rail'} aria-label="Main navigation">
      <div className="rail-top">
        <div className="rail-mark" onClick={toggleNav} title="Expand navigation">
          <img src="/evove-icon.jpeg" alt="Evove" />
        </div>
        <div className="rail-fulllogo">
          <img src="/evove-logo.png" alt="Evove" />
        </div>
        <button className="rail-collapse-btn" onClick={toggleNav} title="Collapse navigation" aria-label="Collapse navigation">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <polyline points="13.5 8.5 9.5 12 13.5 15.5" />
          </svg>
        </button>
      </div>

      <RailLink to="/" label="My Timesheet">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="3" y="4" width="18" height="18" rx="2"></rect><line x1="3" y1="10" x2="21" y2="10"></line></svg>
      </RailLink>

      {canApprove && !SOFT_LAUNCH.enabled && (
        <RailLink to="/approvals" label="My Approvals" chip={approvalCount}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path></svg>
        </RailLink>
      )}

      {isSuperApprover && !SOFT_LAUNCH.enabled && (
        <RailLink to="/super-approvals" label="Super Approver" chip={superApprovalCount}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
        </RailLink>
      )}

      {isSoftLaunchApprover && (
        <RailLink to="/sophies-approvals" label="Approvals" chip={softLaunchApprovalCount}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
        </RailLink>
      )}

      {isUnlockGroup && (
        <RailLink to="/unlock-timesheets" label="Unlock Timesheets">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 9.9-1" /></svg>
        </RailLink>
      )}

      {canViewAssetTimesheets && (
        <RailLink to="/asset-timesheets" label="Asset Timesheets">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>
        </RailLink>
      )}

      <RailLink to="/ordering" label="Ordering" chip={fyiCount + pendingOrderCount}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" /></svg>
      </RailLink>

      {(isUnlockGroup || isAdmin) && (
        <RailLink to="/reports" label="Reports">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
        </RailLink>
      )}

      {((isUnlockGroup || isAdmin) || canViewBillOutRates) && (
        <div className="rail-sec">Admin</div>
      )}
      {(isUnlockGroup || isAdmin) && (
        <>
          <RailLink to="/admin/projects" label="Projects & Tasks">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /></svg>
          </RailLink>
          <RailLink to="/admin/people" label="People & Hierarchy">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="23" y1="11" x2="17" y2="11" /><line x1="20" y1="8" x2="20" y2="14" /></svg>
          </RailLink>
        </>
      )}
      {canViewBillOutRates && (
        <RailLink to="/admin/rates" label="Bill-Out Rates">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
        </RailLink>
      )}

      <div className="rail-spacer" />

      <RailLink to="/notifications" label="Notifications" chip={notificationCount}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
      </RailLink>

      <button className="rail-btn help-rail-btn" onClick={() => window.open('/ordering-help.html', '_blank', 'noopener,noreferrer')} title="Ordering Help">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
          <line x1="3" y1="6" x2="21" y2="6" />
          <path d="M16 10a4 4 0 0 1-8 0" />
        </svg>
        <span className="rail-btn-lbl">Ordering Help</span>
        <span className="rail-tip">Ordering Help</span>
      </button>

      <button className="rail-btn help-rail-btn" onClick={() => window.open('/timesheet-help.html', '_blank', 'noopener,noreferrer')} title="Timesheet Help">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <span className="rail-btn-lbl">Timesheet Help</span>
        <span className="rail-tip">Timesheet Help</span>
      </button>

      <div className="rail-divider" />

      <div className="rail-foot">
        <div className="av" title={userName}>{userName.charAt(0).toUpperCase()}</div>
        <div className="rail-foot-text">
          <div className="rail-foot-name">{userName}</div>
        </div>
      </div>

    </nav>
  );
}

function RailLink({ to, label, chip, children }: { to: string; label: string; chip?: number; children: ReactNode }) {
  const hasChip = chip != null && chip > 0;
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) => `rail-btn${isActive ? ' active' : ''}`}
    >
      {children}
      <span className="rail-btn-lbl">{label}</span>
      {hasChip && <span className="rail-btn-chip">{chip}</span>}
      {hasChip && <span className="rail-badge" />}
      <span className="rail-tip">{label}{hasChip ? ` (${chip})` : ''}</span>
    </NavLink>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

function AppShell() {
  const { person, isLoading, error, canApprove, isSuperApprover, isUnlockGroup, isAdmin, canViewAssetTimesheets, canViewBillOutRates, reload: reloadCurrentUser } =
    useCurrentUser();
  const managerApprovals = useManagerApprovals(
    person?.m365Email ?? '',
    person?.fullName ?? ''
  );
  const isSoftLaunchApprover = SOFT_LAUNCH.enabled && !!person?.m365Email &&
    SOFT_LAUNCH.superApprovers.some(e => e.toLowerCase() === person!.m365Email.trim().toLowerCase());
  const superApprovals = useSuperApprovals(person?.m365Email ?? '', isSoftLaunchApprover);
  const { orders: fyiOrders, reload: reloadSidebarFYI } = useFYIOrders(person?.m365Email ?? '');
  const { orders: pendingOrderApprovals, reload: reloadSidebarOrders } = usePendingApprovals(
    person?.m365Email ?? '',
    person?.purchaseApprovalLimit ?? null,
    {
      isCFO: person?.isCFO,
      isChemicalApprover: person?.isChemicalApprover,
      isOMGroup: person?.isOrderMgmtGroup,
      isOBOCEO: person?.canApproveOnBehalfOfCEO,
    }
  );
  const notifications = useNotifications(person?.m365Email ?? '');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const titleMap: Record<string, string> = {
      '/': 'Evove Timesheets',
      '/approvals': 'My Approvals — Evove',
      '/super-approvals': 'Super Approver — Evove',
      '/sophies-approvals': 'Approvals — Evove',
      '/unlock-timesheets': 'Unlock Timesheets — Evove',
      '/asset-timesheets': 'Asset Timesheets — Evove',
      '/ordering': 'Evove Ordering',
      '/reports': 'Reports — Evove',
      '/notifications': 'Notifications — Evove',
      '/admin/projects': 'Projects & Tasks — Evove',
      '/admin/people': 'People & Hierarchy — Evove',
      '/admin/rates': 'Bill-Out Rates — Evove',
    };
    document.title = titleMap[location.pathname] ?? 'Evove Timesheets';
  }, [location.pathname]);

  if (isLoading) return <LoadingScreen />;
  if (error) {
    const isAccessError =
      error.message.includes('not found in PEOPLE list') ||
      error.message.includes('403');
    return isAccessError ? <AccessDeniedScreen error={error} /> : <ErrorScreen error={error} />;
  }
  if (!person) return <ErrorScreen error={new Error('No user loaded')} />;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
      <Sidebar
        canApprove={canApprove}
        isSuperApprover={isSuperApprover}
        isUnlockGroup={isUnlockGroup}
        isAdmin={isAdmin}
        canViewAssetTimesheets={canViewAssetTimesheets}
        canViewBillOutRates={canViewBillOutRates}
        userName={person.fullName}
        approvalCount={canApprove ? managerApprovals.pendingItems.length : 0}
        superApprovalCount={isSuperApprover ? superApprovals.pendingItems.length : 0}
        isSoftLaunchApprover={isSoftLaunchApprover}
        softLaunchApprovalCount={isSoftLaunchApprover ? superApprovals.pendingItems.length : 0}
        fyiCount={fyiOrders.length}
        pendingOrderCount={pendingOrderApprovals.length}
        notificationCount={notifications.unseenCount}
      />

      <NotificationToastContainer
        toasts={notifications.toasts}
        onDismiss={notifications.dismissToast}
        onNavigate={() => navigate('/notifications')}
      />

      <main style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        <Routes>
          <Route
            path="/"
            element={
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <MyTimesheet
                  userEmail={person.m365Email}
                  userName={person.fullName}
                  currentPerson={person}
                  canActAsManager={canApprove}
                  canActAsSuperApprover={isSuperApprover}
                  canActAsUnlockGroup={isUnlockGroup}
                />
              </div>
            }
          />

          {canApprove && !SOFT_LAUNCH.enabled && (
            <Route
              path="/approvals"
              element={
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <ApprovalDashboard managerEmail={person.m365Email} managerName={person.fullName} approvals={managerApprovals} />
                </div>
              }
            />
          )}

          {isSuperApprover && !SOFT_LAUNCH.enabled && (
            <Route
              path="/super-approvals"
              element={
                <SuperApproverDashboard approverEmail={person.m365Email} approverName={person.fullName} approvals={superApprovals} />
              }
            />
          )}

          {isSoftLaunchApprover && (
            <Route
              path="/sophies-approvals"
              element={
                <SuperApproverDashboard
                  approverEmail={person.m365Email}
                  approverName={person.fullName}
                  softLaunchMode={true}
                  approvals={superApprovals}
                />
              }
            />
          )}

          {isUnlockGroup && (
            <Route
              path="/unlock-timesheets"
              element={
                <UnlockGroupDashboard unlockedByName={person.fullName} unlockedByEmail={person.m365Email} />
              }
            />
          )}

          {canViewAssetTimesheets && (
            <Route
              path="/asset-timesheets"
              element={
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <AssetTimesheets />
                </div>
              }
            />
          )}

          {(isUnlockGroup || isAdmin) && (
            <Route
              path="/reports"
              element={
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <Reports />
                </div>
              }
            />
          )}

          {(isUnlockGroup || isAdmin) && (
            <>
              <Route path="/admin" element={<Navigate to="/admin/projects" replace />} />
              <Route path="/admin/projects" element={<ProjectsAdmin />} />
              <Route path="/admin/people" element={<PeopleAdmin />} />
            </>
          )}
          {canViewBillOutRates && (
            <Route path="/admin/rates" element={<RatesAdmin />} />
          )}

          <Route
            path="/ordering"
            element={
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <Ordering
                  currentPerson={person}
                  onRefreshUser={reloadCurrentUser}
                  onRefreshSidebar={() => { reloadSidebarOrders(); reloadSidebarFYI(); }}
                />
              </div>
            }
          />

          <Route
            path="/notifications"
            element={
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <NotificationsPage notifications={notifications} />
              </div>
            }
          />

          {/* Redirect any unknown path */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <HashRouter>
      <AppShell />
    </HashRouter>
  );
}
